import api from './api';
import { type Recipe, type RecipeFormData } from '../types/recipe';
import { cache } from '../utils/cache';
import { normalizeListResponse } from '../utils/listResponse';

// Types for API request/response
interface TastingNotesDTO {
  acidity: number;
  aftertaste: number;
  clean_cup: number;
  head: number;
  fragrance: number;
  sweetness: number;
  balance: number;
  aroma: number;
  body: number;
  flavor_tags: string[];
  notes?: string;
}

interface RecipeProfilePointDTO {
  id: string;
  recipe_id: string;
  sequence_order: number;
  time_seconds: number;
  temperature: number;
  fire?: number;
  airflow?: number;
  air_temp?: number | null;
  drum_temp?: number | null;
  environment_temp?: number | null;
  drum_rpm?: number | null;
}

interface StageSummaryEntryDTO {
  time: string;
  temp: number | string;
}

interface RecipeDTO {
  id: string;
  company_id: string;
  name: string;
  description: string;
  target_weight_input: number;
  target_weight_output?: number;
  expected_shrinkage?: number;
  machine?: string;
  machine_type_id?: string;
  roast_type?: string;
  item_id?: string;
  item_name?: string;
  item_code?: string;
  agtron_whole?: number;
  agtron_ground?: number;
  stage_summary?: Record<string, StageSummaryEntryDTO>;
  expected_weight_loss_percentage: number;
  total_time_seconds: number;
  shelf_life?: number;
  version: string;
  is_active: boolean;
  internal_notes?: string;
  created_at: string;
  updated_at: string;
  stages: RecipeProfilePointDTO[];
  tasting_notes?: TastingNotesDTO;
}

interface CreateRecipeRequest {
  name: string;
  description?: string;
  target_weight_input: number;
  target_weight_output?: number;
  expected_shrinkage?: number;
  machine?: string;
  machine_type_id?: string;
  roast_type?: string;
  item_id?: string;
  agtron?: {
    wholeBean?: number;
    groundBean?: number;
  };
  stage_summary?: Record<string, StageSummaryEntryDTO>;
  expected_weight_loss_percentage?: number;
  total_time_seconds: number;
  shelf_life?: number;
  internal_notes?: string;
  stages: CreateProfilePointRequest[];
  tasting_notes?: TastingNotesDTO;
}

interface CreateProfilePointRequest {
  time_seconds: number;
  temperature: number | null;
  fire?: number;
  airflow?: number;
  air_temp?: number | null;
  drum_temp?: number | null;
  environment_temp?: number | null;
  drum_rpm?: number | null;
  sequence_order: number;
}

export interface ListRecipesParams {
  page?: number;
  size?: number;
  search?: string;
}

export const recipeService = {
  getAll: async (): Promise<Recipe[]> => {
    return cache.fetch('recipes_all', async () => {
      const response = await api.get<RecipeDTO[]>('/recipes');
      return response.data.map(mapDtoToRecipe);
    });
  },

  list: async (params: ListRecipesParams = {}): Promise<{ data: Recipe[]; total: number }> => {
    const { page = 1, size = 25, search } = params;
    const query = new URLSearchParams();
    query.set('limit', String(size));
    query.set('offset', String((page - 1) * size));
    query.set('include_meta', 'true');
    if (search && search.trim()) query.set('search', search.trim());
    const response = await api.get(`/recipes?${query.toString()}`);
    const normalized = normalizeListResponse<RecipeDTO>(response.data);
    return { data: normalized.data.map(mapDtoToRecipe), total: normalized.total };
  },

  create: async (data: RecipeFormData): Promise<Recipe> => {
    const requestData: CreateRecipeRequest = {
      name: data.name,
      description: data.description,
      // Convert grams to kg - REMOVED (Input is already in kg)
      target_weight_input: data.targetBeanWeight,
      target_weight_output: data.targetWeightOutput,
      expected_shrinkage: data.expectedShrinkage,
      machine: data.machine,
      machine_type_id: data.machineTypeId,
      roast_type: data.roastType,
      item_id: data.itemId ? data.itemId : undefined,
      agtron: data.agtron ? {
        wholeBean: data.agtron.wholeBean,
        groundBean: data.agtron.groundBean,
      } : undefined,
      stage_summary: data.stageSummary,
      expected_weight_loss_percentage: 0,
      // Convert minutes to seconds
      total_time_seconds: Math.round(data.totalTime * 60),
      internal_notes: data.internalNotes,
      shelf_life: data.shelfLife,
      stages: (data.profile || []).map((point, index) => ({
        time_seconds: point.seconds,
        temperature: point.bean,
        fire: point.fire ?? undefined,
        airflow: point.airflow ?? undefined,
        air_temp: point.airTemp ?? null,
        drum_temp: point.drumTemp ?? null,
        environment_temp: point.environmentTemp ?? null,
        drum_rpm: point.drumRpm ?? null,
        sequence_order: index + 1
      })),
      tasting_notes: data.tastingNotes ? {
        acidity: data.tastingNotes.acidity,
        aftertaste: data.tastingNotes.aftertaste,
        clean_cup: data.tastingNotes.cleanCup,
        head: data.tastingNotes.head,
        fragrance: data.tastingNotes.fragrance,
        sweetness: data.tastingNotes.sweetness,
        balance: data.tastingNotes.balance,
        aroma: data.tastingNotes.aroma,
        body: data.tastingNotes.body,
        flavor_tags: data.tastingNotes.flavorTags,
        notes: data.tastingNotes.notes
      } : undefined
    };

    const response = await api.post<RecipeDTO>('/recipes', requestData);
    cache.invalidate('recipes');
    return mapDtoToRecipe(response.data);
  },

  update: async (id: string, data: RecipeFormData): Promise<Recipe> => {
    const requestData: CreateRecipeRequest = {
      name: data.name,
      description: data.description,
      // Convert grams to kg - REMOVED (Input is already in kg)
      target_weight_input: data.targetBeanWeight,
      target_weight_output: data.targetWeightOutput,
      expected_shrinkage: data.expectedShrinkage,
      machine: data.machine,
      machine_type_id: data.machineTypeId,
      roast_type: data.roastType,
      item_id: data.itemId ? data.itemId : undefined,
      agtron: data.agtron ? {
        wholeBean: data.agtron.wholeBean,
        groundBean: data.agtron.groundBean,
      } : undefined,
      stage_summary: data.stageSummary,
      expected_weight_loss_percentage: 0,
      // Convert minutes to seconds
      total_time_seconds: Math.round(data.totalTime * 60),
      shelf_life: data.shelfLife,
      internal_notes: data.internalNotes,
      stages: (data.profile || []).map((point, index) => ({
        time_seconds: point.seconds,
        temperature: point.bean,
        fire: point.fire ?? undefined,
        airflow: point.airflow ?? undefined,
        air_temp: point.airTemp ?? null,
        drum_temp: point.drumTemp ?? null,
        environment_temp: point.environmentTemp ?? null,
        drum_rpm: point.drumRpm ?? null,
        sequence_order: index + 1
      })),
      tasting_notes: data.tastingNotes ? {
        acidity: data.tastingNotes.acidity,
        aftertaste: data.tastingNotes.aftertaste,
        clean_cup: data.tastingNotes.cleanCup,
        head: data.tastingNotes.head,
        fragrance: data.tastingNotes.fragrance,
        sweetness: data.tastingNotes.sweetness,
        balance: data.tastingNotes.balance,
        aroma: data.tastingNotes.aroma,
        body: data.tastingNotes.body,
        flavor_tags: data.tastingNotes.flavorTags,
        notes: data.tastingNotes.notes
      } : undefined
    };

    const response = await api.put<RecipeDTO>(`/recipes/${id}`, requestData);
    cache.invalidate('recipes');
    return mapDtoToRecipe(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/recipes/${id}`);
    cache.invalidate('recipes');
  },

  getById: async (id: string): Promise<Recipe> => {
    return cache.fetch(`recipes_${id}`, async () => {
      const response = await api.get<RecipeDTO>(`/recipes/${id}`);
      return mapDtoToRecipe(response.data);
    });
  }
};

const mapDtoToRecipe = (dto: RecipeDTO): Recipe => {
  const wholeBean =
    typeof dto.agtron_whole === 'number' ? dto.agtron_whole : undefined;
  const groundBean =
    typeof dto.agtron_ground === 'number' ? dto.agtron_ground : undefined;
  const agtron =
    wholeBean !== undefined || groundBean !== undefined
      ? {
          wholeBean,
          groundBean,
          classification: {
            scaa: '',
            common: '',
          },
        }
      : undefined;

  return {
    id: dto.id,
    name: dto.name || '',
    description: dto.description || '',
    targetBeanWeight: dto.target_weight_input || 0,
    targetWeightOutput: dto.target_weight_output || 0,
    expectedShrinkage: dto.expected_shrinkage || 0,
    machine: dto.machine || '',
    machineTypeId: dto.machine_type_id || '',
    roastType: dto.roast_type || '',
    itemId: dto.item_id || '',
    itemName: dto.item_name || '',
    itemCode: dto.item_code || '',
    agtron,
    profile: (dto.stages || []).map(stage => ({
      time: new Date((stage.time_seconds || 0) * 1000)
        .toISOString()
        .substr(14, 5),
      seconds: stage.time_seconds || 0,
      bean: stage.temperature,
      fire: stage.fire || null,
      airflow: stage.airflow || null,
      ror: null,
      airTemp: stage.air_temp ?? null,
      drumTemp: stage.drum_temp ?? null,
      environmentTemp: stage.environment_temp ?? null,
      drumRpm: stage.drum_rpm ?? null,
    })),
    stageSummary: dto.stage_summary ?? {},
    totalTime: dto.total_time_seconds || 0,
    shelfLife: dto.shelf_life || 0,
    version: dto.version || '1.0',
    isActive: dto.is_active !== false,
    internalNotes: dto.internal_notes || '',
    createdAt: dto.created_at || new Date().toISOString(),
    tastingNotes: dto.tasting_notes ? {
      acidity: dto.tasting_notes.acidity,
      aftertaste: dto.tasting_notes.aftertaste,
      cleanCup: dto.tasting_notes.clean_cup,
      head: dto.tasting_notes.head,
      fragrance: dto.tasting_notes.fragrance,
      sweetness: dto.tasting_notes.sweetness,
      balance: dto.tasting_notes.balance,
      aroma: dto.tasting_notes.aroma,
      body: dto.tasting_notes.body,
      flavorTags: dto.tasting_notes.flavor_tags || [],
      notes: dto.tasting_notes.notes
    } : undefined,
    stages: [] // Deprecated but required by type
  };
};
